import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface StatCardProps {
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  icon?: ReactNode;
  variant?: 'indigo' | 'green' | 'amber' | 'rose' | 'sky' | 'slate';
  change?: {
    value: string | number;
    trend?: 'up' | 'down' | 'neutral';
  };
  sub?: ReactNode;
  className?: string;
  onClick?: () => void;
}

const CHIP_VARIANTS = {
  indigo: 'bg-brand-50 text-brand dark:bg-brand-50/15 dark:text-brand-tint',
  green: 'bg-success-50 text-success dark:bg-emerald-950/40 dark:text-emerald-400',
  amber: 'bg-warning-50 text-warning dark:bg-amber-950/40 dark:text-amber-400',
  rose: 'bg-danger-50 text-danger dark:bg-rose-950/40 dark:text-rose-400',
  sky: 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400',
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

export function StatCard({
  label,
  value,
  caption,
  icon,
  variant = 'indigo',
  change,
  sub,
  className,
  onClick,
}: StatCardProps) {
  const Component = onClick ? 'button' : 'div';

  return (
    <Component
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'group flex w-full flex-col justify-between rounded-card border border-line bg-white p-4 shadow-card text-start transition-all',
        'dark:border-slate-800 dark:bg-slate-900',
        onClick && 'cursor-pointer hover:-translate-y-0.5 hover:shadow-md hover:border-brand/30',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="block text-xs font-semibold text-ink-3 dark:text-slate-400 truncate">
            {label}
          </span>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className="text-xl font-bold tracking-tight text-ink dark:text-slate-100">
              {value}
            </span>
            {sub && (
              <span className="text-xs font-medium text-ink-3 dark:text-slate-400">{sub}</span>
            )}
          </div>
        </div>

        {icon && (
          <div
            className={cn(
              'grid h-10 w-10 shrink-0 place-items-center rounded-full transition-transform group-hover:scale-105',
              CHIP_VARIANTS[variant],
            )}
            aria-hidden
          >
            {icon}
          </div>
        )}
      </div>

      {(caption || change) && (
        <div className="mt-3 flex items-center gap-1.5 text-[11px] text-ink-3 dark:text-slate-400">
          {change && (
            <span
              className={cn(
                'font-bold',
                change.trend === 'up' && 'text-success',
                change.trend === 'down' && 'text-danger',
                change.trend === 'neutral' && 'text-ink-3',
              )}
            >
              {change.trend === 'up' ? '↑ ' : change.trend === 'down' ? '↓ ' : ''}
              {change.value}
            </span>
          )}
          {caption && <span>{caption}</span>}
        </div>
      )}
    </Component>
  );
}

export default StatCard;
